import React, { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import type { ValidationIssue } from '../../utils/surveyValidation';

interface SurveyValidationAlertProps {
  issues: ValidationIssue[];
  onFocusQuestion?: (questionId: string) => void;
  onFocusPath?: (pathId: string) => void;
}

export function SurveyValidationAlert({
  issues,
  onFocusQuestion,
  onFocusPath,
}: SurveyValidationAlertProps) {
  const [expanded, setExpanded] = useState(true);
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');

  if (issues.length === 0) return null;

  return (
    <Alert
      variant={errors.length > 0 ? 'destructive' : 'default'}
      className="mb-2"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 font-semibold">
          {errors.length > 0 && (
            <>
              <IconAlertCircle className="size-4" />
              <span>{errors.length} error{errors.length !== 1 ? 's' : ''}</span>
            </>
          )}
          {errors.length > 0 && warnings.length > 0 && <span> · </span>}
          {warnings.length > 0 && (
            <>
              <IconAlertTriangle className="size-4" />
              <span>{warnings.length} warning{warnings.length !== 1 ? 's' : ''}</span>
            </>
          )}
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide' : 'Show details'}
        </Button>
      </div>

      {expanded && (
        <ul className="mt-2 space-y-1">
          {issues.map((issue, idx) => (
            <li
              key={`${issue.code}-${issue.questionId ?? issue.pathId ?? idx}`}
              className="flex items-start justify-between gap-2 py-0.5"
            >
              <span
                className={cn(
                  'flex-1 text-sm',
                  issue.severity === 'error' ? 'text-destructive' : 'text-amber-700 dark:text-amber-400'
                )}
              >
                {issue.message}
              </span>
              {(issue.questionId && onFocusQuestion) || (issue.pathId && onFocusPath) ? (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    if (issue.questionId && onFocusQuestion) onFocusQuestion(issue.questionId);
                    else if (issue.pathId && onFocusPath) onFocusPath(issue.pathId);
                  }}
                >
                  Go to
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
}
